import { getFormSnapshot, logFormEvent } from './api';
import { byId, inputById, inputValue } from './dom';
import { clearInlineError, showInlineError } from './errors';
import { autocompleteState, fieldState, multiState } from './state';
import type { AutocompleteFieldId } from './types';

const EMAIL_REGEX = /^[^\s@]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const EMAIL_TYPO_MAP: Record<string, string> = {
  'gnail.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmil.com': 'gmail.com',
  'yaho.com': 'yahoo.com',
  'hotmial.com': 'hotmail.com',
  'hormail.com': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'vp.pl': 'wp.pl',
  'wpp.pl': 'wp.pl',
  'inteira.pl': 'interia.pl',
  'inetria.pl': 'interia.pl',
  'onetc.pl': 'onet.pl',
  'o2.pl.pl': 'o2.pl',
};

export function validateDateYear(inputId: string): boolean {
  const input = inputById(inputId);
  if (!input || !input.value) return true;
  const yearPart = input.value.split('-')[0];
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear + 5;

  if (!/^\d{4}$/.test(yearPart)) {
    showInlineError(inputId, 'The year must consist of exactly 4 digits.');
    return false;
  }
  const year = Number(yearPart);
  if (year < currentYear) {
    showInlineError(inputId, `The year cannot be earlier than ${currentYear}.`);
    return false;
  }
  if (year > maxYear) {
    showInlineError(inputId, `The year cannot be later than ${maxYear}.`);
    return false;
  }
  if (input.min && input.value < input.min) {
    showInlineError(inputId, `Date cannot be earlier than ${input.min} (minimum 3 days in advance).`);
    return false;
  }

  clearInlineError(inputId);
  return true;
}

export function validateReturnTimeAfterPickup(): boolean {
  const pickupDate = inputValue('pickupDate');
  const pickupTime = inputValue('pickupTime');
  const returnDate = inputValue('returnDate');
  const returnTime = inputValue('returnTime');

  clearInlineError('returnTime');
  if (!returnDate || !returnTime || !pickupDate || !pickupTime) return true;

  if (pickupDate === returnDate) {
    const [pickupH, pickupM] = pickupTime.split(':').map(Number);
    const [returnH, returnM] = returnTime.split(':').map(Number);
    const pickupMinutes = pickupH * 60 + pickupM;
    const returnMinutes = returnH * 60 + returnM;

    if (returnMinutes - pickupMinutes < 60) {
      fieldState.returnTime.touched = true;
      showInlineError('returnTime', 'Return time must be at least 1 hour later than pickup time when on the same day.');
      return false;
    }
  }
  return true;
}

export function updateReturnFieldsState(): void {
  const pickupDate = inputById('pickupDate');
  const pickupTime = inputById('pickupTime');
  const returnDate = inputById('returnDate');
  const returnTime = inputById('returnTime');

  if (!pickupDate || !pickupTime || !returnDate || !returnTime) return;

  const pickupDateTimeSelected = Boolean(pickupDate.value && pickupTime.value);
  returnDate.disabled = !pickupDateTimeSelected;
  returnTime.disabled = !pickupDateTimeSelected;

  if (!pickupDateTimeSelected) {
    returnDate.value = '';
    returnTime.value = '';
  }
}

function validateRequired(id: string): boolean {
  const input = inputById(id);
  if (!input) return true;
  clearInlineError(id);
  if (!input.value.trim() && input.hasAttribute('required')) {
    showInlineError(id, 'This field is required.');
    return false;
  }
  return true;
}

const SAME_ADDRESS_MESSAGE = 'Pickup and destination addresses cannot be the same.';
const STOPOVER_DUPLICATE_MESSAGE = 'Stopover cannot be the same as the pickup, destination or another stopover.';

/**
 * Pickup i destination nie mogą być tym samym adresem.
 * Zwraca false i pokazuje błąd pod polem destination, gdy oba adresy są identyczne
 * (porównanie niewrażliwe na wielkość liter i białe znaki na końcach).
 */
function checkAddressesDistinct(pickup: string, destination: string, destErrorId: string): boolean {
  const p = pickup.trim().toLowerCase();
  const d = destination.trim().toLowerCase();
  if (p && p === d) {
    showInlineError(destErrorId, SAME_ADDRESS_MESSAGE);
    return false;
  }
  return true;
}

function validateStandardTrip(): boolean {
  let isValid = true;

  const autocompleteFields: AutocompleteFieldId[] = ['pickup', 'destination', 'returnPickup', 'returnDestination'];
  autocompleteFields.forEach((id) => {
    const input = inputById(id);
    if (!input) return;
    clearInlineError(id);
    if (!input.value.trim() && input.hasAttribute('required')) {
      showInlineError(id, 'This field is required.');
      isValid = false;
    } else if (input.value.trim() && !autocompleteState[id]) {
      showInlineError(id, 'Please select the full street address from the dropdown.');
      isValid = false;
    }
  });

  if (!checkAddressesDistinct(inputValue('pickup'), inputValue('destination'), 'destination')) {
    isValid = false;
  }
  if (
    inputValue('tripType') === 'return' &&
    !checkAddressesDistinct(inputValue('returnPickup'), inputValue('returnDestination'), 'returnDestination')
  ) {
    isValid = false;
  }

  (['pickupDate', 'pickupTime', 'returnDate', 'returnTime'] as const).forEach((id) => {
    if (!validateRequired(id)) isValid = false;
  });

  (['pickupDate', 'returnDate'] as const).forEach((id) => {
    if (!validateDateYear(id)) isValid = false;
  });

  if (inputValue('tripType') === 'return' && !validateReturnTimeAfterPickup()) isValid = false;

  return isValid;
}

function validateMultiTrips(): boolean {
  if (multiState.trips.length === 0) return false;

  let isValid = true;
  multiState.trips.forEach((t) => {
    const mPickupId = `m_pickup_${t.id}`;
    const mDestId = `m_dest_${t.id}`;
    const mDateId = `m_date_${t.id}`;
    const mTimeId = `m_time_${t.id}`;

    clearInlineError(mPickupId);
    if (!t.pickup.trim()) {
      showInlineError(mPickupId, 'This field is required.');
      isValid = false;
    } else if (!t.pickupValid) {
      showInlineError(mPickupId, 'Please select a specific address.');
      isValid = false;
    }

    clearInlineError(mDestId);
    if (!t.destination.trim()) {
      showInlineError(mDestId, 'This field is required.');
      isValid = false;
    } else if (!t.destValid) {
      showInlineError(mDestId, 'Please select a specific address.');
      isValid = false;
    } else if (!checkAddressesDistinct(t.pickup, t.destination, mDestId)) {
      isValid = false;
    }

    clearInlineError(mDateId);
    if (!t.date.trim()) {
      showInlineError(mDateId, 'This field is required.');
      isValid = false;
    } else if (!validateDateYear(mDateId)) {
      isValid = false;
    }

    clearInlineError(mTimeId);
    if (!t.time.trim()) {
      showInlineError(mTimeId, 'This field is required.');
      isValid = false;
    }

    t.stopovers.forEach((stop, sIdx) => {
      const mStopId = `m_stop_${t.id}_${sIdx}`;
      clearInlineError(mStopId);
      const stopVal = stop.trim().toLowerCase();
      if (stop.trim() && !t.stopoversValid[sIdx]) {
        showInlineError(mStopId, 'Please select a specific address.');
        isValid = false;
      } else if (stopVal) {
        // Przystanek nie może być tym samym adresem co pickup, destination ani inny przystanek.
        const otherAddresses = [t.pickup, t.destination, ...t.stopovers.filter((_, i) => i !== sIdx)].map((a) =>
          a.trim().toLowerCase(),
        );
        if (otherAddresses.includes(stopVal)) {
          showInlineError(mStopId, STOPOVER_DUPLICATE_MESSAGE);
          isValid = false;
        }
      }
    });
  });
  return isValid;
}

function validateContactFields(): boolean {
  let isValid = true;

  (['fullName', 'email'] as const).forEach((id) => {
    if (!validateRequired(id)) isValid = false;
  });

  const emailInput = inputById('email');
  if (emailInput?.value.trim()) {
    const emailVal = emailInput.value.trim().toLowerCase();
    if (!EMAIL_REGEX.test(emailInput.value.trim())) {
      showInlineError('email', 'Please enter a valid email address.');
      isValid = false;
    } else {
      const domain = emailVal.split('@')[1];
      const suggestedDomain = EMAIL_TYPO_MAP[domain];
      if (suggestedDomain) {
        const suggestedEmail = emailVal.replace(domain, suggestedDomain);
        showInlineError('email', `Did you mean ${suggestedEmail}?`);
        isValid = false;
      }
    }
  }

  const phoneInput = inputById('phone');
  clearInlineError('phone');
  if (phoneInput) {
    if (!phoneInput.value.trim() && phoneInput.hasAttribute('required')) {
      showInlineError('phone', 'This field is required.');
      isValid = false;
    } else if (phoneInput.value.trim() && window.iti && !window.iti.isValidNumber()) {
      showInlineError('phone', 'An incorrect telephone number has been entered.');
      isValid = false;
    }
  }

  return isValid;
}

// --- GŁÓWNA FUNKCJA WALIDACJI ---
export function validateForm(): boolean {
  const isMulti = inputValue('tripType') === 'multitrip';

  let isFormValid = isMulti ? validateMultiTrips() : validateStandardTrip();
  if (!validateContactFields()) isFormValid = false;
  if (!inputById('terms')?.checked) isFormValid = false;

  const submitBtn = byId<HTMLButtonElement>('submitBtn');
  if (submitBtn) {
    submitBtn.disabled = !isFormValid;
    submitBtn.classList.toggle('primary', isFormValid);
  }

  if (!isFormValid) {
    void logFormEvent('validation_failed', 'Formularz nie przeszedł walidacji', { data: getFormSnapshot() });
  } else {
    void logFormEvent('validation_passed', 'Formularz poprawnie zweryfikowany', { data: getFormSnapshot() });
  }

  return isFormValid;
}
